import { useState } from 'react';
import {
  Car,
  CheckCircle,
  ClipboardCheck,
  Clock,
  Eye,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  User,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { extractErrorMessage } from '@/lib/api-client';
import { formatDateTime, formatRelative } from '@/lib/format';
import {
  useChecklistExecutions,
  useChecklistTemplates,
  useDeleteChecklistTemplate,
} from './api';
import { CATEGORY_LABELS, type ChecklistTemplate } from './types';
import { ChecklistTemplateFormDialog } from './checklist-template-form-dialog';
import { ChecklistTemplateDetailDialog } from './checklist-template-detail-dialog';

export function ChecklistsPage() {
  const [search, setSearch] = useState('');
  const [execSearch, setExecSearch] = useState('');
  const [execStatus, setExecStatus] = useState<'all' | 'in_progress' | 'completed' | 'cancelled'>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState<ChecklistTemplate | null>(null);
  const [editTarget, setEditTarget] = useState<ChecklistTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ChecklistTemplate | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedExecSearch = useDebouncedValue(execSearch, 300);

  const templatesQuery = useChecklistTemplates({
    page: 1,
    limit: 50,
    q: debouncedSearch || undefined,
  });
  const executionsQuery = useChecklistExecutions({
    page: 1,
    limit: 50,
    q: debouncedExecSearch || undefined,
    status: execStatus === 'all' ? undefined : execStatus,
  });
  const deleteTemplate = useDeleteChecklistTemplate();

  const templates = templatesQuery.data?.data ?? [];
  const executions = executionsQuery.data?.data ?? [];

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplate.mutateAsync(deleteTarget.id);
      toast.success(`Template "${deleteTarget.name}" removido`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <ClipboardCheck className="size-3.5" /> Operação
          </div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Checklists</h1>
          <p className="text-sm text-muted-foreground">
            Modelos de inspeção e histórico de execuções.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          Novo modelo
        </Button>
      </header>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates">
            <ClipboardCheck className="size-4" /> Modelos ({templates.length})
          </TabsTrigger>
          <TabsTrigger value="executions">
            <Clock className="size-4" /> Execuções ({executions.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab: Modelos de checklist */}
        <TabsContent value="templates">
          <Card>
            <CardContent className="p-4">
              <div className="relative mb-4">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar template..."
                  className="pl-9"
                />
              </div>

              {templatesQuery.isLoading && (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              )}

              {!templatesQuery.isLoading && templates.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-12 text-center">
                  <ClipboardCheck className="size-10 text-muted-foreground" />
                  <p className="text-sm font-medium">Nenhum modelo de checklist</p>
                  <p className="text-xs text-muted-foreground">
                    Clique em "Novo modelo" para criar seu primeiro checklist.
                  </p>
                  <Button onClick={() => setCreateOpen(true)} className="mt-2">
                    <Plus className="size-4" />
                    Novo modelo
                  </Button>
                </div>
              )}

              <div className="space-y-3">
                {templates.map((t) => (
                  <Card key={t.id}>
                    <CardContent className="flex items-center justify-between gap-4 p-4">
                      <button
                        type="button"
                        onClick={() => setDetailTarget(t)}
                        className="flex flex-1 items-center gap-3 text-left transition-opacity hover:opacity-80"
                      >
                        <div className="flex size-11 items-center justify-center rounded-xl bg-success/10 text-success">
                          <ClipboardCheck className="size-5" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{t.name}</p>
                          <p className="text-xs text-muted-foreground">
                            <Badge variant="secondary" className="mr-1.5">
                              {CATEGORY_LABELS[t.category] ?? t.category}
                            </Badge>
                            {t.items?.length ?? 0} itens
                            {t.description && ` · ${t.description}`}
                          </p>
                        </div>
                      </button>
                      <div className="flex items-center gap-2">
                        {t.isActive ? (
                          <Badge variant="success">Ativo</Badge>
                        ) : (
                          <Badge variant="outline">Inativo</Badge>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="size-8">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setDetailTarget(t)}>
                              <Eye className="size-4" /> Ver detalhes
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditTarget(t)}>
                              <Pencil className="size-4" /> Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeleteTarget(t)}
                              className="text-destructive"
                            >
                              <Trash2 className="size-4" /> Remover
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Execuções */}
        <TabsContent value="executions">
          <Card>
            <CardContent className="p-4">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={execSearch}
                    onChange={(e) => setExecSearch(e.target.value)}
                    placeholder="Buscar por placa, motorista ou modelo de checklist..."
                    className="pl-9"
                  />
                </div>
                <Select
                  value={execStatus}
                  onValueChange={(v) =>
                    setExecStatus(v as typeof execStatus)
                  }
                >
                  <SelectTrigger className="sm:w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="in_progress">Em andamento</SelectItem>
                    <SelectItem value="completed">Concluído</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {executionsQuery.isLoading && (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              )}

              {!executionsQuery.isLoading && executions.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-12 text-center">
                  <Clock className="size-10 text-muted-foreground" />
                  <p className="text-sm font-medium">
                    {execSearch || execStatus !== 'all'
                      ? 'Nenhuma execução encontrada'
                      : 'Nenhuma execução ainda'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {execSearch || execStatus !== 'all'
                      ? 'Ajuste a busca ou o filtro para ver mais resultados.'
                      : 'As execuções aparecem aqui quando motoristas completam checklists no app.'}
                  </p>
                </div>
              )}

              {executions.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-4 py-3 font-medium">Data</th>
                        <th className="px-4 py-3 font-medium">Veículo</th>
                        <th className="px-4 py-3 font-medium">Executor</th>
                        <th className="px-4 py-3 font-medium">Modelo</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium text-center">OK</th>
                        <th className="px-4 py-3 font-medium text-center">NOK</th>
                        <th className="px-4 py-3 font-medium text-center">N/A</th>
                        <th className="px-4 py-3 font-medium text-center">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {executions.map((e) => (
                        <tr key={e.id} className="transition-colors hover:bg-muted/30">
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium">
                                {formatDateTime(e.startedAt)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatRelative(e.startedAt)}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {e.vehicle ? (
                              <div className="flex items-start gap-2">
                                <Car className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                                <div className="min-w-0">
                                  <p className="font-mono text-xs font-semibold">
                                    {e.vehicle.plate}
                                  </p>
                                  {(e.vehicle.brand || e.vehicle.model) && (
                                    <p className="truncate text-[11px] text-muted-foreground">
                                      {[e.vehicle.brand, e.vehicle.model]
                                        .filter(Boolean)
                                        .join(' ')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 max-w-[200px]">
                            {e.driver ? (
                              <div className="flex items-start gap-2">
                                <User className="mt-0.5 size-3.5 shrink-0 text-success" />
                                <div className="min-w-0">
                                  <p className="truncate text-xs font-medium">
                                    {e.driver.name}
                                  </p>
                                  <p className="text-[11px] text-muted-foreground">
                                    Motorista · CNH {e.driver.cnhCategory}
                                  </p>
                                </div>
                              </div>
                            ) : e.executor ? (
                              <div className="flex items-start gap-2">
                                <User className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                                <div className="min-w-0">
                                  <p className="truncate text-xs font-medium">
                                    {e.executor.name}
                                  </p>
                                  <p className="text-[11px] capitalize text-muted-foreground">
                                    {e.executor.role}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 max-w-[200px]">
                            <p className="truncate text-xs font-medium">
                              {e.template?.name ?? '—'}
                            </p>
                            {e.template?.category && (
                              <p className="text-[11px] text-muted-foreground">
                                {CATEGORY_LABELS[e.template.category] ??
                                  e.template.category}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {e.status === 'completed' ? (
                              <Badge variant="success">
                                <CheckCircle className="mr-1 size-3" /> Concluído
                              </Badge>
                            ) : e.status === 'in_progress' ? (
                              <Badge variant="warning">
                                <Clock className="mr-1 size-3" /> Em andamento
                              </Badge>
                            ) : (
                              <Badge variant="outline">
                                <XCircle className="mr-1 size-3" /> Cancelado
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="font-mono text-sm font-semibold text-success">
                              {e.okCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`font-mono text-sm font-semibold ${
                                e.nokCount > 0 ? 'text-destructive' : 'text-muted-foreground'
                              }`}
                            >
                              {e.nokCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="font-mono text-sm text-muted-foreground">
                              {e.naCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="font-mono text-sm">{e.totalItems}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ChecklistTemplateFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ChecklistTemplateFormDialog
        open={!!editTarget}
        onOpenChange={(open) => !open && setEditTarget(null)}
        template={editTarget}
      />
      <ChecklistTemplateDetailDialog
        open={!!detailTarget}
        onOpenChange={(open) => !open && setDetailTarget(null)}
        template={detailTarget}
        onEdit={() => {
          if (detailTarget) {
            setEditTarget(detailTarget);
            setDetailTarget(null);
          }
        }}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remover modelo</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover "{deleteTarget?.name}"? Execuções passadas são mantidas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              loading={deleteTemplate.isPending}
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
